import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Container } from 'react-bootstrap';
import { useHistory } from 'react-router-dom';
import { FiEye, FiUpload, FiHome } from 'react-icons/fi';
import CompanyPickerModal from './Batch/CompanyPickerModal';

import BasePage from '../../components/BasePage';
import DataTable, { Column, FilterField } from '../../components/DataTable';

import api from '../../services/api';
import { useAuth } from '../../hooks/auth';
import { formatCpf } from '../../utils/cpfMask';

import { StatusBadge, ActionButton, BatchButton } from './styles';

interface IWorker {
  id: string;
  first_name: string;
  last_name: string;
  cpf: string;
  phone: string;
  activity_profile: string;
  is_syndicate_approved?: boolean | null;
  is_unionized?: boolean;
  syndicate?: { id?: string; nome_fantasia?: string };
}

interface IUnion {
  id: string;
  nome_fantasia: string;
}

interface IState {
  id: number;
  uf: string;
}

interface ICompany {
  id: string;
  nome_fantasia: string;
  cnpj?: string;
}

const ACTIVITY_LABELS: Record<string, string> = {
  industrial: 'Industrial',
  rural: 'Rural',
  market: 'Comércio',
  'public-service': 'Serviço Público',
};

const STATUS_OPTIONS = [
  { value: 'true', label: 'Aprovado' },
  { value: 'false', label: 'Pendente' },
  { value: 'null', label: 'Não associado' },
];

const TYPE_OPTIONS = [
  { value: 'industrial', label: 'Industrial' },
  { value: 'rural', label: 'Rural' },
  { value: 'market', label: 'Comércio' },
  { value: 'public-service', label: 'Serviço Público' },
];

const cpfMask = (value: string): string => {
  const digits = value.replace(/\D/g, '').slice(0, 11);
  return digits
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d)/, '$1.$2')
    .replace(/(\d{3})(\d{1,2})$/, '$1-$2');
};

const Workers: React.FC = () => {
  const { user } = useAuth();
  const history = useHistory();

  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [workers, setWorkers] = useState<IWorker[]>([]);
  const [totalWorkers, setTotalWorkers] = useState(0);
  const [filterParams, setFilterParams] = useState<Record<string, string>>({});
  const [unions, setUnions] = useState<IUnion[]>([]);
  const [states, setStates] = useState<IState[]>([]);
  const [companies, setCompanies] = useState<ICompany[]>([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const companyOnChangeRef = useRef<((v: string) => void) | null>(null);

  useEffect(() => {
    document.title = 'Trabalhadores - Viva Voz - Rede Suco de Laranja';
  }, []);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const response = await api.get('/workers', {
          params: { ...filterParams, page: currentPage },
        });
        if (cancelled) return;
        setWorkers(response.data);
        setTotalWorkers(Number(response.headers['x-total-workers'] || 0));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [currentPage, filterParams]);

  useEffect(() => {
    const loadAux = async () => {
      const companiesRes = await api.get<ICompany[]>('/companies', {
        params:
          user.role !== 'admin'
            ? { syndicate_id: user.syndicate_id, limit: 9999 }
            : { limit: 9999 },
      });
      setCompanies(companiesRes.data);

      if (user.role === 'admin') {
        const [unionsRes, statesRes] = await Promise.all([
          api.get<IUnion[]>('/unions'),
          api.get<IState[]>('/ibge/states'),
        ]);
        setUnions(unionsRes.data);
        setStates(statesRes.data);
      }
    };
    loadAux();
  }, [user.role, user.syndicate_id]);

  const filters = useMemo<FilterField[]>(() => {
    const base: FilterField[] = [
      {
        key: 'name',
        label: 'Nome',
        type: 'text',
        placeholder: 'Digite o nome',
        span: 3,
      },
      {
        key: 'cpf',
        label: 'CPF',
        type: 'text',
        placeholder: '000.000.000-00',
        mask: cpfMask,
        maxLength: 14,
        span: 2,
      },
      {
        key: 'activity_profile',
        label: 'Categoria',
        type: 'select',
        placeholder: 'Todas',
        options: TYPE_OPTIONS,
        span: 2,
      },
      {
        key: 'is_syndicate_approved',
        label: 'Status',
        type: 'select',
        placeholder: 'Todos',
        options: STATUS_OPTIONS,
        span: 2,
      },
    ];

    base.push({
      key: 'company_id',
      label: 'Empresa',
      type: 'custom',
      placeholder: 'Todas',
      options: companies.map(c => ({ value: c.id, label: c.nome_fantasia })),
      span: 2,
      render: (value, onChange) => {
        companyOnChangeRef.current = onChange;
        const selected = companies.find(c => c.id === value);
        return (
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              width: '100%',
              height: 36,
              padding: '0 10px',
              border: '1px solid #e2e8f0',
              borderRadius: 6,
              background: '#fff',
              cursor: 'pointer',
              fontSize: 13,
              fontFamily: 'inherit',
              color: selected ? '#0f172a' : '#94a3b8',
              textAlign: 'left',
            }}
          >
            <FiHome size={13} style={{ color: '#94a3b8', flexShrink: 0 }} />
            <span
              style={{
                flex: 1,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {selected ? selected.nome_fantasia : 'Todas'}
            </span>
          </button>
        );
      },
    });

    if (user.role === 'admin') {
      base.push({
        key: 'syndicate_id',
        label: 'Sindicato',
        type: 'select',
        placeholder: 'Todos',
        options: unions.map(u => ({ value: u.id, label: u.nome_fantasia })),
        span: 2,
      });
      base.push({
        key: 'state',
        label: 'Estado',
        type: 'select',
        placeholder: 'Todos',
        options: states.map(s => ({ value: s.uf, label: s.uf })),
        span: 1,
      });
    }

    return base;
  }, [user.role, user.syndicate_id, unions, states, companies]);

  const columns = useMemo<Column<IWorker>[]>(
    () => [
      {
        key: 'name',
        label: 'Nome',
        render: w => (
          <span style={{ fontWeight: 600, color: '#0f172a' }}>
            {`${w.first_name} ${w.last_name}`}
          </span>
        ),
      },
      {
        key: 'cpf',
        label: 'CPF',
        width: '15%',
        render: w => formatCpf(w.cpf),
      },
      {
        key: 'phone',
        label: 'Telefone',
        width: '15%',
        render: w => w.phone || '—',
      },
      {
        key: 'activity_profile',
        label: 'Categoria',
        width: '12%',
        render: w => ACTIVITY_LABELS[w.activity_profile] || '—',
      },
      {
        key: 'status',
        label: 'Status',
        width: '14%',
        render: w => {
          if (w.is_syndicate_approved === true)
            return <StatusBadge $tone="success">Aprovado</StatusBadge>;
          if (w.is_syndicate_approved === false)
            return <StatusBadge $tone="warning">Pendente</StatusBadge>;
          return <StatusBadge $tone="neutral">Não associado</StatusBadge>;
        },
      },
    ],
    [],
  );

  const handleFiltersChange = useCallback((values: Record<string, string>) => {
    setFilterParams(values);
    setCurrentPage(1);
  }, []);

  const batchButton = (
    <BatchButton type="button" onClick={() => history.push('/workers/batch')}>
      <FiUpload size={14} />
      Cadastro em Lote
    </BatchButton>
  );

  return (
    <BasePage title="Trabalhadores">
      {pickerOpen && (
        <CompanyPickerModal
          companies={companies}
          selectedId={filterParams.company_id || ''}
          onConfirm={id => {
            companyOnChangeRef.current?.(id);
            setPickerOpen(false);
          }}
          onClose={() => setPickerOpen(false)}
        />
      )}
      <Container fluid style={{ padding: 0 }}>
        <DataTable<IWorker>
          title="Trabalhadores"
          subtitle="Gerencie o cadastro, vínculos sindicais e status dos colaboradores"
          columns={columns}
          data={workers}
          rowKey="id"
          loading={loading}
          page={currentPage}
          pageSize={25}
          total={totalWorkers}
          onPageChange={setCurrentPage}
          filters={filters}
          filterValues={filterParams}
          onFiltersChange={handleFiltersChange}
          headerActions={batchButton}
          rowActions={row => (
            <ActionButton
              type="button"
              onClick={() => history.push(`/workers/${row.id}`)}
            >
              <FiEye size={14} />
              Visualizar
            </ActionButton>
          )}
          emptyMessage="Nenhum trabalhador encontrado."
        />
      </Container>
    </BasePage>
  );
};

export default Workers;
